import * as React from "react"
import { Minus, Plus } from "lucide-react"
import { FONTS, cssFamily, getFont, type FontKey } from "@/lib/label-canvas/fonts"
import { PALETTE, paletteColour } from "@/lib/label-canvas/palette"
import { LABEL_MM, type BottleType } from "@/lib/label-canvas/schema"
import { cn } from "@/lib/utils"

// Small, page-specific inspector controls for the label studio. They use the
// Private Cellar hairline language from public-studio.css. SSR-safe.

// ---------------------------------------------------------------- Bottles

function WineSilhouette() {
  return (
    <svg viewBox="0 0 24 72" fill="none" aria-hidden="true">
      <path
        d="M10 2h4v16c0 3 5 6 5 12v38a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V30c0-6 5-9 5-12V2Z"
        stroke="currentColor"
        strokeWidth="1.1"
      />
      <rect x="6.2" y="40" width="11.6" height="14" stroke="currentColor" strokeWidth="0.8" opacity="0.7" />
    </svg>
  )
}

function MccSilhouette() {
  return (
    <svg viewBox="0 0 24 72" fill="none" aria-hidden="true">
      <path
        d="M9.5 2h5v4l-.5 1v8c0 5 6.5 10 6.5 17v36a2 2 0 0 1-2 2H5.5a2 2 0 0 1-2-2V32c0-7 6.5-12 6.5-17V7l-.5-1V2Z"
        stroke="currentColor"
        strokeWidth="1.1"
      />
      <rect x="4.8" y="44" width="14.4" height="16" stroke="currentColor" strokeWidth="0.8" opacity="0.7" />
    </svg>
  )
}

const BOTTLE_CHOICES: { value: BottleType; name: string; Icon: () => React.ReactElement }[] = [
  { value: "wine", name: "Wine", Icon: WineSilhouette },
  { value: "mcc", name: "MCC", Icon: MccSilhouette },
]

export function BottleTiles({
  value,
  onChange,
}: {
  value: BottleType
  onChange: (bottle: BottleType) => void
}) {
  return (
    <div className="studio-bottles" role="group" aria-label="Bottle">
      {BOTTLE_CHOICES.map(({ value: bottle, name, Icon }) => {
        const mm = LABEL_MM[bottle]
        return (
          <button
            key={bottle}
            type="button"
            className="studio-bottle"
            aria-pressed={value === bottle}
            onClick={() => onChange(bottle)}
          >
            <Icon />
            <span className="studio-bottle-name">{name}</span>
            <span className="studio-bottle-size">
              {mm.width} × {mm.height} mm label
            </span>
          </button>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------- Fonts

/** The curated font list, each name drawn in its own face (a sample, not chrome). */
export function FontList({ value, onChange }: { value: FontKey; onChange: (font: FontKey) => void }) {
  return (
    <div className="studio-fonts" role="group" aria-label="Font">
      {FONTS.map((font) => (
        <button
          key={font.key}
          type="button"
          className="studio-font"
          aria-pressed={value === font.key}
          onClick={() => onChange(font.key)}
        >
          <span className="studio-font-sample" style={{ fontFamily: cssFamily(font.key) }}>
            {font.label}
          </span>
          <span className="studio-font-note">{font.description}</span>
        </button>
      ))}
    </div>
  )
}

export function fontSupports(key: FontKey) {
  const font = getFont(key)
  return { bold: font.supportsBold, italic: font.supportsItalic }
}

// ---------------------------------------------------------------- Stepper

/** − value + for decimal values (mm, em). Rounds to the step's precision. */
export function DecimalStepper({
  value,
  onChange,
  step,
  min,
  max,
  label,
  format,
  id,
}: {
  value: number
  onChange: (next: number) => void
  step: number
  min: number
  max: number
  label: string
  format: (value: number) => string
  id?: string
}) {
  const decimals = (String(step).split(".")[1] ?? "").length
  const round = (n: number) => Number(n.toFixed(decimals))
  const commit = (n: number) => onChange(round(Math.min(max, Math.max(min, n))))
  return (
    <div className="studio-stepper" role="group" aria-label={label} id={id}>
      <button
        type="button"
        onClick={() => commit(value - step)}
        disabled={value <= min}
        aria-label={`Decrease ${label.toLowerCase()}`}
      >
        <Minus aria-hidden="true" />
      </button>
      <output aria-live="polite">{format(value)}</output>
      <button
        type="button"
        onClick={() => commit(value + step)}
        disabled={value >= max}
        aria-label={`Increase ${label.toLowerCase()}`}
      >
        <Plus aria-hidden="true" />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------- Swatches

/** The seven palette colours; names on hover (title) and to assistive tech. */
export function Swatches({
  value,
  onChange,
  label,
  allowNone = false,
}: {
  value: string | null
  onChange: (hex: string | null) => void
  label: string
  allowNone?: boolean
}) {
  const [hover, setHover] = React.useState<string | null>(null)
  const current = paletteColour(value)
  const shown = hover ?? (value === null && allowNone ? "No fill" : current?.label ?? "")
  return (
    <div>
      <div className="studio-swatches" role="group" aria-label={label}>
        {allowNone && (
          <button
            type="button"
            className="studio-swatch studio-swatch-none"
            title="No fill"
            aria-label="No fill"
            aria-pressed={value === null}
            onClick={() => onChange(null)}
            onMouseEnter={() => setHover("No fill")}
            onMouseLeave={() => setHover(null)}
          />
        )}
        {PALETTE.map((colour) => (
          <button
            key={colour.key}
            type="button"
            className="studio-swatch"
            style={{ backgroundColor: colour.hex }}
            title={colour.label}
            aria-label={colour.label}
            aria-pressed={value !== null && value.toLowerCase() === colour.hex}
            onClick={() => onChange(colour.hex)}
            onMouseEnter={() => setHover(colour.label)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </div>
      <div className={cn("studio-swatch-name")} aria-hidden="true">
        {shown}
      </div>
    </div>
  )
}
